

import React, { useState } from 'react';
import { Globe, Database, Play, CheckCircle2, Loader2, FileText, XCircle, Settings, ChevronDown, ChevronUp, BookOpen, Layout } from 'lucide-react';
// Corrected import path for types.ts
import { Sector, Article, ArticleStatus, User, UserRole, Attachment } from '@/types'; // Corrected import path
import { supabase } from "@/supabaseClient";

interface MigrationToolProps {
  sectors: Sector[];
  users: User[];
}

export const MigrationTool: React.FC<MigrationToolProps> = ({ sectors, users }) => {
  const [url, setUrl] = useState('https://wiki.soescritorio.com.br');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  
  // Advanced Settings
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [postType, setPostType] = useState('posts'); // Default: posts
  const [taxonomy, setTaxonomy] = useState('categories'); // Default: categories
  
  const [status, setStatus] = useState<'idle' | 'extracting' | 'normalizing' | 'indexing' | 'completed' | 'error'>('idle');
  const [logs, setLogs] = useState<string[]>([]);
  const [progress, setProgress] = useState(0);

  const addLog = (msg: string) => {
    setLogs(prev => [...prev, `[${new Date().toLocaleTimeString()}] ${msg}`]);
    const consoleEl = document.getElementById('migration-console');
    if (consoleEl) consoleEl.scrollTop = consoleEl.scrollHeight;
  };

  const applyPreset = (type: 'standard' | 'betterdocs') => {
    if (type === 'betterdocs') {
      setPostType('docs');
      setTaxonomy('doc_category');
      addLog("Configuração ajustada para plugin BetterDocs (Endpoint: /docs)");
    } else {
      setPostType('posts');
      setTaxonomy('categories');
      addLog("Configuração ajustada para WordPress Padrão (Endpoint: /posts)");
    }
  };

  // Helper to guess mime type
  const getMimeType = (extension: string) => {
    const map: Record<string, string> = {
      'pdf': 'application/pdf',
      'doc': 'application/msword',
      'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'xls': 'application/vnd.ms-excel',
      'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'png': 'image/png',
      'jpg': 'image/jpeg',
      'jpeg': 'image/jpeg',
      'zip': 'application/zip'
    };
    if (Object.prototype.hasOwnProperty.call(map, extension)) {
      return map[extension];
    }
    return 'application/octet-stream';
  };

  // Advanced HTML Processor: Extracts files and converts to Markdown
  const processHtmlContent = (html: string): { markdown: string, attachments: Attachment[] } => {
    if (!html) return { markdown: "", attachments: [] };

    // 1. Parse HTML to DOM
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const attachments: Attachment[] = [];

    // 2. Extract File Links (Anchors)
    // We look for links that end in document extensions
    const anchors = doc.querySelectorAll('a');
    anchors.forEach(a => {
        const href = a.getAttribute('href');
        if (!href) return;

        const extension = href.split('.').pop()?.toLowerCase();
        const isFile = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'zip', 'rar'].includes(extension || '');
        
        // Check if it looks like a downloadable file
        if (isFile) {
            attachments.push({
                id: `mig-${Math.random().toString(36).substr(2, 9)}`,
                name: a.textContent?.trim() || href.split('/').pop() || 'Arquivo Anexo',
                url: href,
                type: getMimeType(extension || '')
            });
            // Remove the node from DOM so the text "Baixar" doesn't appear in the middle of the article
            a.remove();
        }
    });

    // 3. Extract Images (Optional - for now we treat them as content, but could be attachments too)
    // If you want images to appear as attachments, uncomment below:
    /*
    const images = doc.querySelectorAll('img');
    images.forEach(img => {
        const src = img.getAttribute('src');
        if (src) {
             attachments.push({
                id: `img-${Math.random().toString(36).substr(2, 9)}`,
                name: img.getAttribute('alt') || 'Imagem',
                url: src,
                type: 'image/jpeg'
            });
            // img.remove(); // Remove from text if moving to attachments
        }
    });
    */

    // 4. Convert remaining DOM to string for regex processing
    let text = doc.body.innerHTML;

    // 5. Decode Entities & Clean HTML
    text = text.replace(/&#8211;/g, '-');
    text = text.replace(/&#8212;/g, '--');
    text = text.replace(/&nbsp;/g, ' ');
    
    // Structural Cleanup
    text = text.replace(/<br\s*\/?>/gi, '\n');
    text = text.replace(/<\/p>/gi, '\n\n');
    text = text.replace(/<p>/gi, '');
    text = text.replace(/<h1>/gi, '# ');
    text = text.replace(/<\/h1>/gi, '\n');
    text = text.replace(/<h2>/gi, '## ');
    text = text.replace(/<\/h2>/gi, '\n');
    text = text.replace(/<h3>/gi, '### ');
    text = text.replace(/<\/h3>/gi, '\n');
    text = text.replace(/<strong>/gi, '**');
    text = text.replace(/<\/strong>/gi, '**');
    text = text.replace(/<b>/gi, '**');
    text = text.replace(/<\/b>/gi, '**');
    text = text.replace(/<li>/gi, '- ');
    text = text.replace(/<\/li>/gi, '\n');
    
    // Final strip of tags safely
    const tmp = document.createElement("DIV");
    // Use textContent instead of innerHTML to prevent DOM-based XSS
    tmp.textContent = text;
    let cleanText = tmp.innerHTML || "";

    return { markdown: cleanText.trim(), attachments };
  };

  const runMigration = async () => {
    // Basic validation
    let cleanUrl = url.replace(/\/$/, ''); // Remove trailing slash
    if (!cleanUrl.startsWith('http')) {
        cleanUrl = 'https://' + cleanUrl;
    }

    setStatus('extracting');
    setLogs([]);
    setProgress(0);
    addLog(`Iniciando conexão com ${cleanUrl}...`);

    try {
      // 1. Prepare Headers (Basic Auth if provided)
      const headers: HeadersInit = {};
      if (username && password) {
        headers['Authorization'] = 'Basic ' + btoa(`${username}:${password}`);
      }

      // 2. Fetch Categories first to map sectors
      const catEndpoint = `${cleanUrl}/wp-json/wp/v2/${taxonomy}?per_page=100`;
      addLog(`Buscando taxonomia em: /wp-json/wp/v2/${taxonomy}...`);
      
      let categoryMap: Record<number, string> = {};
      
      try {
        const categoriesRes = await fetch(catEndpoint, { headers });
        if (categoriesRes.ok) {
           const categories = await categoriesRes.json();
           addLog(`${categories.length} categorias encontradas.`);
           
           // Create Map: WP Category ID -> App Sector ID
           categories.forEach((cat: any) => {
             // Simple fuzzy matching by name
             const match = sectors.find(s => s.name.toLowerCase().includes(cat.name.toLowerCase()) || cat.name.toLowerCase().includes(s.name.toLowerCase()));
             if (match) {
                 categoryMap[cat.id] = match.id;
             }
           });
        } else {
           addLog(`Aviso: Falha ao buscar categorias (${categoriesRes.status}). Ignorando mapeamento de setores.`);
        }
      } catch (catErr) {
         addLog(`Aviso: Erro ao buscar categorias. Endpoint pode não existir.`);
      }

      // 3. Fetch Authors/Users from WordPress
      const usersEndpoint = `${cleanUrl}/wp-json/wp/v2/users?per_page=100`;
      addLog(`Buscando autores em: /wp-json/wp/v2/users...`);
      let wpAuthorMap: Record<number, { name: string, slug: string }> = {};

      try {
        const usersRes = await fetch(usersEndpoint, { headers });
        if (usersRes.ok) {
            const wpUsers = await usersRes.json();
            addLog(`${wpUsers.length} autores encontrados no WordPress.`);
            wpUsers.forEach((u: any) => {
                wpAuthorMap[u.id] = { name: u.name, slug: u.slug };
            });
        } else {
            addLog(`Aviso: Não foi possível listar autores (${usersRes.status}). Usando administrador padrão.`);
        }
      } catch (userErr) {
          addLog(`Aviso: Erro ao buscar autores. Usando administrador padrão.`);
      }

      // Fallback sector and user
      const defaultSectorId = sectors.length > 0 ? sectors[0].id : '';
      const fallbackAdminUser = users.find(u => u.role === UserRole.ADMIN) || users[0];

      // 4. Fetch Posts
      const postsEndpoint = `${cleanUrl}/wp-json/wp/v2/${postType}?per_page=100`;
      addLog(`Buscando conteúdo em: /wp-json/wp/v2/${postType}...`);
      
      const postsRes = await fetch(postsEndpoint, { headers });
      
      if (!postsRes.ok) throw new Error(`Erro ao buscar conteúdo (Status: ${postsRes.status}). Verifique se o endpoint '${postType}' existe.`);
      
      const posts = await postsRes.json();
      addLog(`${posts.length} itens encontrados.`);

      if (posts.length === 0) {
        addLog("Nenhum item encontrado.");
        addLog("DICA: Certifique-se de escolher a opção 'Plugin BetterDocs' nas configurações acima.");
        setStatus('error');
        return;
      }

      setStatus('normalizing');
      addLog("Normalizando dados e extraindo anexos...");

      let processedCount = 0;
      setStatus('indexing');
      
      // 5. Process and Save each post
      for (const post of posts) {
        addLog(`Processando: ${post.title.rendered}...`);
        
        // Convert Content and Extract Attachments
        const { markdown, attachments } = processHtmlContent(post.content.rendered);
        
        const cleanTitle = processHtmlContent(post.title.rendered).markdown; // Just to clean entities from title
        
        // Determine Sector
        let articleSectorId = defaultSectorId;
        // Prevent Object Injection
        const postCategories = Object.prototype.hasOwnProperty.call(post, taxonomy) ? post[taxonomy] : (post.categories || []);

        if (Array.isArray(postCategories) && postCategories.length > 0) {
            for (const catId of postCategories) {
                if (Object.prototype.hasOwnProperty.call(categoryMap, catId)) {
                    articleSectorId = categoryMap[catId];
                    break;
                }
            }
        }
        const sectorName = sectors.find(s => s.id === articleSectorId)?.name || 'Geral';

        // Determine Author
        let authorId = fallbackAdminUser?.id || 'migration-bot';
        let authorName = fallbackAdminUser?.fullName || 'Sistema';

        // Check if we have WP Author Data
        if (post.author && wpAuthorMap[post.author]) {
            const wpAuthor = wpAuthorMap[post.author];
            authorName = wpAuthor.name; // Preserve original name

            // Try to match with existing App User
            const matchingAppUser = users.find(u => 
                u.username.toLowerCase() === wpAuthor.slug.toLowerCase() || 
                u.fullName.toLowerCase() === wpAuthor.name.toLowerCase()
            );

            if (matchingAppUser) {
                authorId = matchingAppUser.id;
            } else {
                authorId = `wp-legacy-${post.author}`; 
            }
        }

        // Create Article Object
        const newArticle: Article = {
            id: `wp-${post.id}`, // Use WP ID to prevent duplicates on re-run
            title: cleanTitle,
            content: markdown,
            authorId: authorId,
            authorName: authorName,
            sectorId: articleSectorId,
            sectorName: sectorName,
            status: ArticleStatus.APPROVED, // Auto-approve migrated content
            createdAt: post.date ? new Date(post.date).toISOString() : new Date().toISOString(),
            updatedAt: post.modified ? new Date(post.modified).toISOString() : new Date().toISOString(),
            tags: ['Migrado', 'WordPress', postType],
            attachments: attachments // Found attachments
        };

        // Save to Supabase directly
        const { error: upsertError } = await supabase
          .from('articles')
          .upsert({
            id: newArticle.id,
            title: newArticle.title,
            content: newArticle.content,
            author_id: newArticle.authorId,
            author_name: newArticle.authorName,
            sector_id: newArticle.sectorId || null,
            sector_name: newArticle.sectorName,
            status: newArticle.status,
            created_at: newArticle.createdAt,
            updated_at: newArticle.updatedAt,
            tags: newArticle.tags,
            attachments: newArticle.attachments
          });
        
        if (upsertError) throw upsertError;
        
        processedCount++;
        setProgress(Math.round((processedCount / posts.length) * 100));
        
        // Small delay to prevent rate limiting/browser freeze
        await new Promise(r => setTimeout(r, 50));
      }

      addLog(`Sucesso! ${processedCount} itens foram migrados e indexados.`);
      setStatus('completed');

    } catch (error: any) {
      console.error(error);
      addLog(`ERRO FATAL: ${error.message}`);
      if (error.message.includes('Failed to fetch') || error.name === 'TypeError') {
         addLog("DICA: Isso geralmente é um erro de CORS ou URL inválvida.");
      }
      setStatus('error');
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
      <div className="mb-6 flex items-start justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2">
            <Database className="w-5 h-5 text-blue-600" />
            Migração de Conteúdo (WordPress)
          </h2>
          <p className="text-slate-500 text-sm mt-1">Conecta-se à API REST do WordPress para importar conteúdo.</p>
        </div>
        <div className="bg-blue-50 text-blue-800 text-xs px-3 py-1 rounded-full font-medium">
          Extractor v2.0
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Configuration Form */}
        <div className="space-y-4">
          <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 space-y-3">
            <h3 className="font-semibold text-slate-700 text-sm">1. Origem dos Dados</h3>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">URL do WordPress</label>
              <div className="flex items-center">
                <Globe className="w-4 h-4 text-slate-400 mr-2" />
                <input
                  type="text"
                  value={url}
                  onChange={e => setUrl(e.target.value)}
                  placeholder="https://meusite.com.br"
                  className="w-full text-sm bg-white border border-slate-300 rounded px-2 py-1"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Usuário (Opcional)</label>
                <input
                  type="text"
                  value={username}
                  onChange={e => setUsername(e.target.value)}
                  placeholder="admin"
                  className="w-full text-sm bg-white border border-slate-300 rounded px-2 py-1"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Senha (App Password)</label>
                <input
                  type="password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="xxxx xxxx xxxx xxxx"
                  className="w-full text-sm bg-white border border-slate-300 rounded px-2 py-1"
                />
              </div>
            </div>

            <div className="pt-2 border-t border-slate-200 mt-2">
               <h3 className="font-semibold text-slate-700 text-sm mb-2">2. Tipo de Wiki</h3>
               <div className="grid grid-cols-2 gap-2">
                 <button
                   onClick={() => applyPreset('standard')}
                   className={`p-2 text-xs font-medium rounded border text-left flex items-center gap-2 transition-colors ${
                     postType === 'posts' ? 'bg-blue-100 border-blue-300 text-blue-800' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-100'
                   }`}
                 >
                   <Layout className="w-4 h-4" />
                   Padrão (Posts)
                 </button>
                 <button
                   onClick={() => applyPreset('betterdocs')}
                   className={`p-2 text-xs font-medium rounded border text-left flex items-center gap-2 transition-colors ${
                     postType === 'docs' ? 'bg-blue-100 border-blue-300 text-blue-800' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-100'
                   }`}
                 >
                   <BookOpen className="w-4 h-4" />
                   Plugin BetterDocs
                 </button>
               </div>
            </div>

            {/* Advanced Options Toggle */}
            <div className="pt-2">
               <button 
                 onClick={() => setShowAdvanced(!showAdvanced)}
                 className="flex items-center gap-1 text-xs text-blue-600 font-medium hover:underline"
               >
                 <Settings className="w-3 h-3" />
                 {showAdvanced ? 'Ocultar Detalhes Técnicos' : 'Mostrar Detalhes Técnicos'}
                 {showAdvanced ? <ChevronUp className="w-3 h-3"/> : <ChevronDown className="w-3 h-3"/>}
               </button>

               {showAdvanced && (
                 <div className="mt-3 grid grid-cols-2 gap-3 p-3 bg-white border border-slate-200 rounded animate-fade-in">
                   <div>
                      <label className="block text-[10px] font-bold text-slate-500 mb-1 uppercase">Endpoint de Conteúdo</label>
                      <input 
                        type="text" 
                        value={postType}
                        onChange={e => setPostType(e.target.value)}
                        className="w-full text-xs border border-slate-300 rounded px-2 py-1"
                        placeholder="ex: posts, docs"
                      />
                   </div>
                   <div>
                      <label className="block text-[10px] font-bold text-slate-500 mb-1 uppercase">Endpoint de Categoria</label>
                      <input 
                        type="text" 
                        value={taxonomy}
                        onChange={e => setTaxonomy(e.target.value)}
                        className="w-full text-xs border border-slate-300 rounded px-2 py-1"
                        placeholder="ex: categories, doc_category"
                      />
                   </div>
                 </div>
               )}
            </div>
          </div>

          <button
            onClick={runMigration}
            disabled={status === 'extracting' || status === 'indexing'}
            className={`w-full flex items-center justify-center gap-2 py-2 rounded-lg font-medium transition-colors ${
              status === 'extracting' || status === 'indexing'
                ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                : 'bg-blue-600 hover:bg-blue-700 text-white'
            }`}
          >
            {status === 'extracting' || status === 'indexing' ? (
                <>
                    <Loader2 className="w-4 h-4 animate-spin" /> Processando {progress}%
                </>
            ) : (
                <>
                    <Play className="w-4 h-4" /> Iniciar Migração
                </>
            )}
          </button>

          {status === 'completed' && (
             <div className="bg-green-50 border border-green-200 text-green-700 p-3 rounded-lg flex items-center gap-2 text-sm">
               <CheckCircle2 className="w-5 h-5" />
               Migração finalizada! Os artigos foram limpos e indexados.
             </div>
          )}
          
          {status === 'error' && (
             <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-lg flex items-center gap-2 text-sm">
               <XCircle className="w-5 h-5 flex-shrink-0" />
               A migração encontrou problemas. Verifique o console.
             </div>
          )}
        </div>

        {/* Console Log */}
        <div id="migration-console" className="bg-slate-900 rounded-lg p-4 font-mono text-xs text-green-400 h-64 overflow-y-auto shadow-inner scroll-smooth">
          <div className="flex items-center gap-2 text-slate-400 border-b border-slate-700 pb-2 mb-2">
            <FileText className="w-4 h-4" />
            <span>Console de Migração</span>
          </div>
          {logs.length === 0 && <span className="text-slate-600">Aguardando início...</span>}
          {logs.map((log, idx) => (
            <div key={idx} className={`mb-1 border-b border-slate-800/50 pb-0.5 ${log.includes('ERRO') || log.includes('DICA') ? 'text-yellow-400 font-bold' : ''}`}>{log}</div>
          ))}
          {(status === 'extracting' || status === 'indexing') && <div className="animate-pulse mt-2">_</div>}
        </div>
      </div>
      
      {/* Visual Pipeline Steps */}
      <div className="mt-8 flex items-center justify-between relative">
        <div className="absolute top-1/2 left-0 w-full h-1 bg-slate-200 -z-10 transform -translate-y-1/2"></div>
        {['REST API', 'Fetch & Auth', 'Extrair Anexos', 'Salvar DB'].map((step, idx) => {
           let active = false;
           if (status === 'extracting' || status === 'normalizing' || status === 'indexing') {
             if (idx === 0 && status === 'extracting') active = true;
             if (idx === 1 && (status === 'extracting' || status === 'normalizing' || status === 'indexing')) active = true;
             if (idx === 2 && (status === 'normalizing' || status === 'indexing')) active = true;
             if (idx === 3 && status === 'indexing') active = true;
           }
           if (status === 'completed') active = true;
           if (status === 'error' && idx <= 1) active = true; // Still active for initial fetch errors

           return (
             <div key={step} className="flex flex-col items-center bg-white px-2">
               <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 transition-colors duration-500 ${active ? 'bg-blue-600 border-blue-600 text-white' : 'bg-white border-slate-300 text-slate-300'}`}>
                 {idx + 1}
               </div>
               <span className={`text-xs mt-1 font-medium ${active ? 'text-blue-700' : 'text-slate-400'}`}>{step}</span>
             </div>
           )
        })}
      </div>
    </div>
  );
};